/**
 * Norwegian Bokmål catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 2.
 *
 * `locale: 'nb'` (Bokmål, the dominant written standard; see DECISIONS for the `nb` vs `no`
 * choice). `dir: 'ltr'`. Native-grade Norwegian at a mathematician's register. **Warrant →
 * «Hjemmel»** — the established Scandinavian rendering of Toulmin's warrant. W/M grades stay
 * Latin (`::numbering-system/latn`). CLDR cardinal categories are one / other, so `frameActive`
 * declares exactly those (plus an explicit `=0`). Per INV-STABLE-ID a wrong translation is a
 * presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const NB_CATALOG = {
    locale: 'nb',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Undersøkelse',
        [P2.sectionClaims]: 'Påstander',
        [P2.sectionIntegrity]: 'Integritet',
        [P2.axisWarrantLabel]: 'Hjemmel',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirisk',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Definisjon} interp {Tolkning} testable {Testbar} tested {Testet} refuted {Gjendrevet} program {Forskningsprogram} philos {Filosofisk} other {Ukjent}}',
        [P2.axisModalLabel]: 'Modal',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Styrkenivå {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Utforskbar',
        [P2.stateRefuted]: 'Gjendrevet',
        [P2.stateOutside]: 'Utenfor området',
        [P2.verdictConflict]: 'I konflikt med {constraints}',
        [P2.verdictOutside]: 'Krever {missing}',
        [P2.lossRegisterPlain]: 'Forenklet til vanlig språk — noe av presisjonen utelates.',
        [P2.lossRegisterKid]: 'Forenklet for yngre lesere — mange detaljer utelates.',
        [P2.lossProfileDetail]: 'Den fullstendige profilen Hjemmel / Empirisk / Modal er oppsummert her.',
        [P2.lossConflictDetail]: 'Konfliktdetaljene er forkortet i denne visningen.',
        [P2.laneVerified]: 'Verifisert',
        [P2.laneUnverified]: 'Ikke verifisert',
        [P2.laneConflict]: 'Konflikt påvist',
        [P2.constraintLogic]: 'Logikk',
        [P2.constraintLightBends]: 'Lys bøyes i et gravitasjonsfelt',
        [P2.constraintParallelPostulate]: 'Parallellpostulatet',
        [P2.claimScalarGravity]: 'Skalar gravitasjonsteori',
        [P2.claimNonEuclidean]: 'Ikke-euklidsk geometri',
        [P2.claimSubstrateSr]: 'Substrattolkning av den spesielle relativitetsteorien',
        [P2.edgeExtObservation]: 'Ekstern observasjon',
        [P2.edgeResourceSample]: 'Delt beregningsressurs',
        [P2.edgeHeuristicSample]: 'Heuristisk veiledning',
        [P2.edgeSharedSample]: 'Felles grunnlag',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Begrensninger',
        [P2.sectionEdges]: 'Kanter',
        [P2.sectionFrame]: 'Ramme',
        [P2.sectionExposure]: 'Eksponering',
        [P2.statusAdoptedFormal]: 'Vedtatt (formell)',
        [P2.statusAdoptedInterpretive]: 'Vedtatt (fortolkende)',
        [P2.statusSuspended]: 'Suspendert',
        [P2.edgeEffectConstraint]: 'Danner en begrensning (kan gjendrive)',
        [P2.edgeEffectResource]: 'Ressurs — ingen virkning på dommer',
        [P2.edgeEffectHeuristic]: 'Heuristikk — ingen virkning på dommer',
        [P2.edgeEffectShared]: 'Felles grunnlag — ingen virkning på dommer',
        [P2.frameActive]: '{count, plural, =0 {Ingen aktive begrensninger} one {# aktiv begrensning} other {# aktive begrensninger}}',
        [P2.exposureNone]: 'Ingen fortolkende avhengigheter',
        [P2.exposureDep]: 'Avhenger av {id}',
        [P2.laneTrackMachine]: 'Maskin',
        [P2.laneTrackExpert]: 'Ekspert',
        [P2.laneTrackCommunity]: 'Fellesskap',
        [P2.expertAudited]: 'Auditert',
        [P2.expertInProgress]: 'Pågår',
        [P2.expertNotReviewed]: 'Ikke gjennomgått',
        [P2.communityFlags]: 'Det finnes merknader',
        [P2.communityNone]: 'Ingen merknader',
        [P2.controlChallenge]: 'Bestrid',
        [P2.controlActivate]: 'Aktiver',
        [P2.controlMakeInterpretive]: 'Gjør fortolkende',
        [P2.controlImportEdge]: 'Importer kant',
        [P2.controlPromote]: 'Forfrem',
        [P2.controlUndo]: 'Angre',
        [P2.controlRedo]: 'Gjør om',
        [P2.controlRegister]: 'Registrer',
        [P2.promoteRejected]: 'Forfremmelse avvist',
        [P2.promoteAccepted]: 'Forfremmelse godtatt',
        [P2.promoteReasonRefutationTerminal]: 'Et bevis kan ikke oppheve en gjendrivelse.',
        [P2.promoteReasonKeystone]: 'En sluttstein-forfremmelse krever empirisk støtte.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Den empiriske typen er ikke fastlagt.',
        [P2.promoteReasonModalNotFixed]: 'Den modale graden er ikke fastlagt.',
        [P2.promoteReasonWarrantNotImproved]: 'Hjemmelsgraden er ikke forbedret.',
        [P2.evidenceComputation]: "{engine} beregnet {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Resultat avrundet til {dp} desimaler",
    },
};
//# sourceMappingURL=nb.js.map